import { useMutation, useQuery, UseQueryResult } from "react-query"
import { HttpRequest } from "../services/_http"

// The media routes have no /api prefix (see "Article and Media API Guide").
const MEDIA_BASE = "/media"

export type MediaStatus = "active" | "used"

export interface MediaItem {
  id: number
  original_name: string
  storage_key: string
  mime_type: string
  file_size: string
  width: number | null
  height: number | null
  alt_text: string | null
  caption: string | null
  status: MediaStatus
  /** Present on upload / GET /media/:id responses: "/media/12/url" */
  url?: string
  active_placement_count?: number
  active_article_count?: number
  created_at?: string
}

export interface MediaLibraryParams {
  search?: string
  status?: MediaStatus | ""
  pageIndex?: number
  pageSize?: number
}

// POST /media/upload  (multipart — do not set Content-Type manually)
export const useUploadMedia = (options?: any) =>
  useMutation(
    ({ file, alt_text }: { file: File; alt_text?: string }) => {
      const form = new FormData()
      form.append("file", file)
      if (alt_text) form.append("alt_text", alt_text.slice(0, 255))

      return HttpRequest({
        url: `${MEDIA_BASE}/upload`,
        method: "POST",
        data: form,
      })
    },
    options
  )

// GET /media/library?search=&status=&pagination[pageIndex]=&pagination[pageSize]=&sort[id]=&sort[desc]=
// The query string is built by hand so the bracket syntax is exact regardless
// of how the shared HTTP client serialises params.
export const useMediaLibrary = (
  { search = "", status = "", pageIndex = 0, pageSize = 12 }: MediaLibraryParams,
  options?: any
): UseQueryResult<any> =>
  useQuery({
    queryKey: ["media-library", search, status, pageIndex, pageSize],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (search.trim()) qs.set("search", search.trim())
      if (status) qs.set("status", status)
      qs.set("pagination[pageIndex]", String(pageIndex))
      qs.set("pagination[pageSize]", String(pageSize))
      qs.set("sort[id]", "created_at")
      qs.set("sort[desc]", "true")

      return HttpRequest({
        url: `${MEDIA_BASE}/library?${qs.toString()}`,
        method: "GET",
      })
    },
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    ...options,
  })